/**
 * Footer — sits at the bottom of the landing + legal pages.
 *
 * Expanded from the earlier thin version to give the "honest by design"
 * positioning real weight in a footer that handles calendar + email +
 * health data. Privacy sits inside its own trust callout so users see the
 * commitment before the linkroll; Contact is a real mailto; legal links
 * live in a bottom rule alongside the copyright.
 */
import { Link } from "react-router-dom";
import { Mail, ShieldCheck, Lock } from "lucide-react";

export function Footer() {
  return (
    <footer className="border-t border-[var(--rammy-border)] bg-[var(--rammy-bg)]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-12 gap-10 text-sm">
        {/* Brand block — logo + one-line positioning + trust callout. */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-2.5" data-testid="footer-logo">
            <div className="w-8 h-8 rounded-xl bg-[var(--rm-ink-900)] flex items-center justify-center">
              <div className="w-2.5 h-2.5 rounded-full bg-[var(--rammy-cyan)]"/>
            </div>
            <span className="font-display text-lg">Rammy</span>
          </div>
          <p className="mt-4 text-[var(--rammy-text-sec)] max-w-sm leading-relaxed">
            The AI companion that remembers your life and acts on it — your calendar,
            your people, the promises you make, and the things you want made.
          </p>

          {/* Privacy commitment callout — bold trust signal for a footer that
              handles calendar/email/health data. Links to /privacy. */}
          <Link
            to="/privacy"
            className="mt-6 inline-flex items-start gap-2.5 rounded-2xl border border-[var(--rammy-border)] bg-[var(--rm-surface)] px-4 py-3 hover:border-[var(--rammy-cyan)]/50 hover:shadow-[0_4px_20px_rgba(17,19,24,0.04)] transition group max-w-sm"
            data-testid="footer-privacy-callout"
          >
            <ShieldCheck size={18} className="text-[var(--rammy-cyan-dark)] mt-0.5 shrink-0" strokeWidth={1.8} />
            <div>
              <div className="font-medium text-[var(--rammy-text)] flex items-center gap-1.5">
                Your data is yours
                <span className="text-[var(--rammy-cyan-dark)] group-hover:translate-x-0.5 transition-transform">→</span>
              </div>
              <div className="text-[13px] text-[var(--rammy-text-sec)] leading-snug mt-0.5">
                Private by default. No ads. No tracking. Every memory yours to edit or delete.
              </div>
            </div>
          </Link>
        </div>

        <div className="md:col-span-2">
          <div className="font-medium mb-3 text-[var(--rammy-text)]">Product</div>
          <ul className="space-y-2.5 text-[var(--rammy-text-sec)]" data-testid="footer-product-links">
            <li><Link to="/app" data-testid="footer-link-app" className="hover:text-[var(--rammy-text)] transition-colors">Web prototype</Link></li>
            <li><Link to="/device" data-testid="footer-link-device" className="hover:text-[var(--rammy-text)] transition-colors">Robot face</Link></li>
            <li><a href="/#pricing" data-testid="footer-link-pricing" className="hover:text-[var(--rammy-text)] transition-colors">Plans</a></li>
            <li><a href="/#hero-features" data-testid="footer-link-features" className="hover:text-[var(--rammy-text)] transition-colors">Features</a></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <div className="font-medium mb-3 text-[var(--rammy-text)]">Company</div>
          <ul className="space-y-2.5 text-[var(--rammy-text-sec)]">
            <li className="text-[var(--rammy-text-muted)]">Prototype v0.1</li>
            <li>
              <a
                href="mailto:hello@rammy.ai"
                className="inline-flex items-center gap-1.5 hover:text-[var(--rammy-text)] transition-colors"
                data-testid="footer-link-contact"
              >
                <Mail size={13} strokeWidth={1.8} /> Contact
              </a>
            </li>
            <li><a href="mailto:careers@rammy.ai" className="hover:text-[var(--rammy-text)] transition-colors">Careers</a></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <div className="font-medium mb-3 text-[var(--rammy-text)]">Legal</div>
          <ul className="space-y-2.5 text-[var(--rammy-text-sec)]">
            <li>
              <Link
                to="/privacy"
                data-testid="footer-link-privacy"
                className="inline-flex items-center gap-1.5 hover:text-[var(--rammy-text)] transition-colors"
              >
                <Lock size={13} strokeWidth={1.8} /> Privacy policy
              </Link>
            </li>
            <li>
              <Link
                to="/terms"
                data-testid="footer-link-terms"
                className="hover:text-[var(--rammy-text)] transition-colors"
              >
                Terms of service
              </Link>
            </li>
            <li>
              <a
                href="mailto:privacy@rammy.ai"
                className="hover:text-[var(--rammy-text)] transition-colors"
                data-testid="footer-link-privacy-mail"
              >
                privacy@rammy.ai
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[var(--rammy-border)]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[var(--rammy-text-muted)]" data-testid="footer-copy">
          <div className="italic text-[var(--rammy-text-sec)]">Everything you tell it, it acts on.</div>
          <div className="flex items-center gap-4">
            <span>© {new Date().getFullYear()} Rammy · rammy.ai</span>
            <span className="hidden sm:inline">·</span>
            <span className="hidden sm:inline">Built with love</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
